import { DocumentFilter, TickCircle } from "iconsax-reactjs"
import { parseAsBoolean, useQueryState } from "nuqs"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"

import { BottomSheetModal } from "@/components/shared"
import { Button } from "@/components/ui"
import {
  transformBillingFilterToQueryParams,
  useFilterBilling
} from "@/modules/helpers/hooks/shared/use-filter-billing"
import { cn } from "@/modules/helpers/hooks/shared/utils"

export function FilterBilling() {
  const [isVisible, setIsVisible] = useQueryState(
    "FilterBilling",
    parseAsBoolean.withDefault(false)
  )

  const { filterState, updateFilterState, resetFilter, countFilter } = useFilterBilling()

  const form = useForm({
    defaultValues: {
      sortBy: "NEWEST",
      acceptanceStatus: "ALL",
      paidType: "ALL"
    }
  })

  // Sync form with filter state on mount
  useEffect(() => {
    if (filterState.order === "DESC") {
      form.setValue("sortBy", "NEWEST")
    }

    if (filterState.order === "ASC") {
      form.setValue("sortBy", "OLDEST")
    }

    if (filterState.acceptanceStatus) {
      form.setValue("acceptanceStatus", filterState.acceptanceStatus)
    }
    if (filterState.paidType) {
      form.setValue("paidType", filterState.paidType)
    }
  }, [filterState.acceptanceStatus, filterState.order, filterState.paidType, form])

  return (
    <>
      <div
        className="rounded-xl bg-white w-10 h-10 flex justify-center items-center relative"
        onClick={() => setIsVisible(true)}
      >
        <DocumentFilter size={20} className="text-primary-800" />
        {countFilter > 0 && (
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {countFilter}
          </div>
        )}
      </div>

      <BottomSheetModal isVisible={isVisible} setIsVisible={setIsVisible} title="Bộ lọc">
        <div className="flex flex-col gap-6 p-2 px-4">
          {/* Separator line */}
          <div className="h-px bg-gray-200 -mt-4 mb-2" />

          {/* Sắp xếp theo */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Sắp xếp theo</label>
            <Controller
              name="sortBy"
              control={form.control}
              render={({ field }) => (
                <div className="flex gap-2">
                  {[
                    { key: "NEWEST", label: "Mới nhất" },
                    { key: "OLDEST", label: "Cũ nhất" }
                  ].map((item) => {
                    const isSelected = field.value === item.key
                    return (
                      <Button
                        key={item.key}
                        variant="outline"
                        size="sm"
                        className={cn(
                          "text-xs rounded-lg flex items-center gap-2 px-3 py-2.5 h-auto",
                          {
                            "bg-white border-gray-300 text-gray-800": isSelected,
                            "bg-white border-gray-300 text-gray-400": !isSelected
                          }
                        )}
                        onClick={() => field.onChange(item.key)}
                      >
                        {isSelected ? (
                          <div className="w-4 h-4 rounded-full flex items-center justify-center">
                            <TickCircle size={12} className="text-secondary-100" variant="Bold" />
                          </div>
                        ) : (
                          <div className="w-4 h-4 rounded-lg border border-gray-300" />
                        )}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              )}
            />
          </div>

          {/* Status */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Trạng thái</label>
            <Controller
              name="acceptanceStatus"
              control={form.control}
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "ALL", label: "Tất cả" },
                    { key: "ACCEPTED", label: "Đã nghiệm thu" },
                    { key: "NOT_ACCEPTED", label: "Chưa nghiệm thu" }
                    // { key: "REJECTED", label: "Từ chối" },
                  ].map((item) => {
                    const isSelected = field.value === item.key
                    return (
                      <Button
                        key={item.key}
                        variant="outline"
                        size="sm"
                        className={cn("text-xs rounded-lg px-4 py-2.5 h-auto", {
                          "bg-gradient-primary border-0 !text-white": isSelected,
                          "bg-white border-gray-300 text-gray-400": !isSelected
                        })}
                        onClick={() => field.onChange(item.key)}
                      >
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              )}
            />
          </div>
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Trạng thái thanh toán</label>
            <Controller
              name="paidType"
              control={form.control}
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "ALL", label: "Tất cả" },
                    { key: "COMPLETED", label: "Đã thanh toán" },
                    { key: "PENDING", label: "Chưa thanh toán" }
                    // { key: "REJECTED", label: "Từ chối" },
                  ].map((item) => {
                    const isSelected = field.value === item.key
                    return (
                      <Button
                        key={item.key}
                        variant="outline"
                        size="sm"
                        className={cn("text-xs rounded-lg px-4 py-2.5 h-auto", {
                          "bg-gradient-primary border-0 !text-white": isSelected,
                          "bg-white border-gray-300 text-gray-400": !isSelected
                        })}
                        onClick={() => field.onChange(item.key)}
                      >
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              )}
            />
          </div>
          {/* Buttons */}
          <div className="flex gap-4 mt-4">
            <Button
              variant="outline"
              className="w-28 text-xs h-12 border-gray-200 bg-white text-gray-700 rounded-xl"
              onClick={() => {
                form.reset({
                  sortBy: "NEWEST",
                  acceptanceStatus: "ALL"
                })
                resetFilter()
              }}
            >
              Đặt lại
            </Button>
            <Button
              className="flex-1 text-xs h-12 rounded-xl bg-primary hover:opacity-90"
              onClick={() => {
                form.handleSubmit((data) => {
                  const filterParams = transformBillingFilterToQueryParams(data)
                  updateFilterState(filterParams)
                  setIsVisible(false)
                })()
              }}
            >
              Xem kết quả
            </Button>
          </div>
        </div>
      </BottomSheetModal>
    </>
  )
}
